/**
 * INTC Advertorial — intc-advertorial.js
 * Handles: spend calculator, variant selector, product tabs, FAQ accordion, video autoplay
 */

(function () {
  'use strict';

  /* -------------------------------------------------------------------------
     Utility: run after DOM ready
  -------------------------------------------------------------------------- */
  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  /* =========================================================================
     1. Spend Calculator
     ========================================================================= */
  function initSpendCalculators() {
    var calculators = document.querySelectorAll('[data-intc-adv-calc]');

    calculators.forEach(function (calc) {
      var slider = calc.querySelector('[data-intc-adv-slider]');
      var annualDisplay = calc.querySelector('[data-intc-adv-annual]');
      var currentDisplay = calc.querySelector('[data-intc-adv-current]');

      if (!slider || !annualDisplay) return;

      var multiplier = parseInt(slider.getAttribute('data-multiplier'), 10) || 12;

      function update() {
        var val = parseInt(slider.value, 10);
        if (currentDisplay) {
          currentDisplay.textContent = val;
        }
        var annual = val * multiplier;
        annualDisplay.textContent = '$' + annual.toLocaleString();
      }

      slider.addEventListener('input', update);
      // Run once on init to set initial state
      update();
    });
  }

  /* =========================================================================
     2. Variant Selector → Scroll + Activate Tab
     ========================================================================= */
  function initVariantSelectors() {
    var rows = document.querySelectorAll('[data-intc-adv-variant-row]');

    rows.forEach(function (row) {
      row.addEventListener('click', function (e) {
        e.preventDefault();

        var variantIndex = row.getAttribute('data-variant-index');
        var scrollTargetId = row.getAttribute('data-scroll-target');

        // Update selected state within this selector card
        var selectorCard = row.closest('[data-intc-adv-selector]');
        if (selectorCard) {
          selectorCard.querySelectorAll('[data-intc-adv-variant-row]').forEach(function (r) {
            r.classList.remove('intc-adv__variant-row--selected');
          });
          row.classList.add('intc-adv__variant-row--selected');
        }

        // Find scroll target element
        if (!scrollTargetId) return;
        var target = document.getElementById(scrollTargetId);
        if (!target) return;

        // Set data attribute so product tabs block can read it
        target.setAttribute('data-selected-variant', variantIndex);

        // Activate the matching tab in the product tabs block
        activateProductTab(target, variantIndex);

        // Smooth scroll to target
        var yOffset = -80; // account for sticky header
        var y = target.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      });
    });
  }

  /* =========================================================================
     3. Product Tabs
     ========================================================================= */
  function initProductTabs() {
    var tabBlocks = document.querySelectorAll('[data-intc-adv-product-tabs]');

    tabBlocks.forEach(function (block) {
      var tabs = block.querySelectorAll('[data-intc-adv-tab]');
      var panels = block.querySelectorAll('[data-intc-adv-panel]');

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var index = tab.getAttribute('data-tab-index');
          activateTabByIndex(block, index);
        });
      });

      // Sync the heading to the server-rendered active panel on load (the
      // static section_heading setting may name a different product).
      var activePanel = block.querySelector('[data-intc-adv-panel].intc-adv__tab-panel--active');
      if (activePanel) {
        updateTabsHeading(block, activePanel.getAttribute('data-panel-index'));
      }
    });
  }

  function activateTabByIndex(block, index) {
    var tabs = block.querySelectorAll('[data-intc-adv-tab]');
    var panels = block.querySelectorAll('[data-intc-adv-panel]');

    // Guard: ignore indexes with no matching panel (e.g. a card grid pointing
    // at a single-tab block) instead of deactivating everything.
    var hasPanel = false;
    panels.forEach(function (p) {
      if (p.getAttribute('data-panel-index') === String(index)) hasPanel = true;
    });
    if (!hasPanel) return;

    tabs.forEach(function (t) {
      t.classList.toggle('intc-adv__tab--active', t.getAttribute('data-tab-index') === String(index));
    });

    panels.forEach(function (p) {
      p.classList.toggle('intc-adv__tab-panel--active', p.getAttribute('data-panel-index') === String(index));
    });

    updateTabsHeading(block, index);
  }

  // Swap the products-block heading to the active tab's product title. The
  // section emits the per-tab titles as a JSON map (data-intc-adv-tab-titles)
  // because the static section_heading setting can only name one product.
  // Preserves whatever heading tag the merchant's setting rendered.
  function updateTabsHeading(block, index) {
    var titlesEl = block.querySelector('script[data-intc-adv-tab-titles]');
    if (!titlesEl) return;
    var titles;
    try {
      titles = JSON.parse(titlesEl.textContent);
    } catch (err) {
      return;
    }
    var title = titles[String(index)];
    if (!title) return;
    var inner = block.closest('.intc-adv__products-inner');
    var headingWrap = inner && inner.querySelector('.intc-adv__products-heading');
    if (!headingWrap) return;
    var headingEl = headingWrap.querySelector('h1,h2,h3,h4,h5,h6') || headingWrap;
    headingEl.textContent = title;
  }

  function activateProductTab(targetElement, variantIndex) {
    // Find product tabs block inside or at the target element
    var block = targetElement.querySelector('[data-intc-adv-product-tabs]');
    if (!block) {
      // Maybe the target IS the tabs block parent
      block = targetElement.closest('[data-intc-adv-product-tabs]') || targetElement;
    }
    if (!block) return;

    activateTabByIndex(block, variantIndex);
  }

  /* =========================================================================
     4. FAQ Accordion
     ========================================================================= */
  function initFaqAccordions() {
    var heads = document.querySelectorAll('[data-intc-adv-faq-head]');

    heads.forEach(function (head) {
      head.addEventListener('click', function () {
        var item = head.closest('[data-intc-adv-faq-item]');
        if (!item) return;

        var content = item.querySelector('[data-intc-adv-faq-content]');
        var isActive = item.classList.contains('intc-adv__faq-item--active');

        // Close all other open items
        document.querySelectorAll('[data-intc-adv-faq-item].intc-adv__faq-item--active').forEach(function (openItem) {
          if (openItem !== item) {
            var openContent = openItem.querySelector('[data-intc-adv-faq-content]');
            if (openContent) openContent.style.maxHeight = '0';
            openItem.classList.remove('intc-adv__faq-item--active');
          }
        });

        if (isActive) {
          if (content) content.style.maxHeight = '0';
          item.classList.remove('intc-adv__faq-item--active');
        } else {
          item.classList.add('intc-adv__faq-item--active');
          if (content) content.style.maxHeight = content.scrollHeight + 'px';
        }
      });
    });
  }

  /* =========================================================================
     5. Video Autoplay / Loop
     ========================================================================= */
  function initVideos() {
    var videos = document.querySelectorAll('[data-intc-adv-video]');

    videos.forEach(function (video) {
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.loop = true;
      video.muted = true;

      // Use IntersectionObserver to play/pause when in view
      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                video.play().catch(function () {
                  // Autoplay may be blocked; silently fail
                });
              } else {
                video.pause();
              }
            });
          },
          { threshold: 0.3 }
        );
        observer.observe(video);
      } else {
        // Fallback: just try to play
        video.play().catch(function () {});
      }
    });
  }

  /* =========================================================================
     6. Variant Price Updater
     Updates price, hidden variant ID, and ATC button when any option changes.
     Reads from the pre-rendered intc-pack-prices-{panelId} JSON on the page.
     ========================================================================= */
  function initVariantPriceUpdater() {
    var variantSelectEls = document.querySelectorAll('variant-selects[data-section^="intc-adv-"]');

    variantSelectEls.forEach(function (vs) {
      var panelId = vs.getAttribute('data-section');
      var pricesEl = document.getElementById('intc-pack-prices-' + panelId);
      var priceContainer = document.getElementById('price-' + panelId);
      var submitBtn = document.getElementById('ProductSubmitButton-' + panelId);

      if (!pricesEl || !priceContainer) return;

      var variants = JSON.parse(pricesEl.textContent.trim());
      var panel = vs.closest('[data-intc-adv-panel]');

      // Map option position → selected value from each fieldset
      function readSelected() {
        var selected = {};
        vs.querySelectorAll('fieldset[data-index]').forEach(function (fs) {
          var pos = parseInt(fs.getAttribute('data-index'), 10);
          var radio = fs.querySelector('input[type="radio"]:checked');
          var select = fs.querySelector('select');
          if (radio) {
            selected[pos] = radio.value;
          } else if (select) {
            selected[pos] = select.value;
          }
        });
        return selected;
      }

      // Grey out Size values with no available variant for the currently
      // selected Pack/Color. The server render only reflects the initial
      // variant and there's no product-info re-render on page templates,
      // so recompute from the variants JSON on load and on every change.
      // Greyed sizes stay clickable (Dawn behavior — ATC shows "Sold out").
      function refreshSizeAvailability() {
        var selected = readSelected();
        vs.querySelectorAll('fieldset[data-index]').forEach(function (fs) {
          var optName = (fs.getAttribute('data-wair-option-name') || '').toLowerCase();
          if (optName.indexOf('size') === -1 || optName.indexOf('pack') !== -1) return;
          var pos = parseInt(fs.getAttribute('data-index'), 10);
          fs.querySelectorAll('input[type="radio"]').forEach(function (input) {
            var anyAvailable = variants.some(function (v) {
              if (v['option' + pos] !== input.value) return false;
              for (var p = 1; p <= 3; p++) {
                if (p === pos) continue;
                if (selected[p] != null && v['option' + p] !== selected[p]) return false;
              }
              return v.available;
            });
            input.classList.toggle('intc-adv-option--oos', !anyAvailable);
            var label = input.id
              ? fs.querySelector(
                  'label[for="' + (window.CSS && CSS.escape ? CSS.escape(input.id) : input.id) + '"]'
                )
              : null;
            if (label) label.classList.toggle('intc-adv-option--oos', !anyAvailable);
          });
        });
      }

      refreshSizeAvailability();

      vs.addEventListener('change', function () {
        var selected = readSelected();

        refreshSizeAvailability();

        // Find the matching variant
        var matched = null;
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var ok = true;
          Object.keys(selected).forEach(function (pos) {
            if (v['option' + pos] !== selected[pos]) ok = false;
          });
          if (ok) { matched = v; break; }
        }
        if (!matched) return;

        // Keep the panel's selected-variant JSON in sync so intc-pack-ladder.js
        // recomputes each pack row against the newly selected size/color
        // (it reads option1..3 from this script tag — see getSelectedVariant()).
        var selectedVariantScript = vs.querySelector('script[data-selected-variant]');
        if (selectedVariantScript) {
          selectedVariantScript.textContent = JSON.stringify(matched);
        }

        // Swap the panel image to the matched variant's image. Sizes of the
        // same color usually share one image, so when the exact variant has
        // none, fall back to the first variant with the same Color value that
        // does; if no image exists at all, keep the current one.
        if (panel) {
          var img = panel.querySelector('.intc-adv__product-img');
          if (img) {
            var imageUrl = matched.image;
            if (!imageUrl) {
              var colorPos = null;
              vs.querySelectorAll('fieldset[data-index]').forEach(function (fs) {
                var nm = (fs.getAttribute('data-wair-option-name') || '').toLowerCase();
                if (nm.indexOf('color') !== -1 || nm.indexOf('colour') !== -1) {
                  colorPos = parseInt(fs.getAttribute('data-index'), 10);
                }
              });
              if (colorPos) {
                for (var ci = 0; ci < variants.length; ci++) {
                  if (
                    variants[ci]['option' + colorPos] === matched['option' + colorPos] &&
                    variants[ci].image
                  ) {
                    imageUrl = variants[ci].image;
                    break;
                  }
                }
              }
            }
            if (imageUrl && img.getAttribute('src') !== imageUrl) {
              img.removeAttribute('srcset');
              img.removeAttribute('sizes');
              img.src = imageUrl;
            }
          }
        }

        // Update price display
        priceContainer.innerHTML =
          '<div class="price price--large"><div class="price__container">' +
          '<span class="price__regular">' +
          '<span class="price-item price-item--regular">' + matched.priceFormatted + '</span>' +
          '</span></div></div>';

        // Update hidden variant id input
        var form = vs.closest('form');
        if (form) {
          var idInput = form.querySelector('[name="id"]');
          if (idInput) idInput.value = matched.id;
        }

        // Update ATC button state
        if (submitBtn) {
          submitBtn.disabled = !matched.available;
          var btnLabel = submitBtn.querySelector('span:not([class*="loading"])');
          if (btnLabel) btnLabel.textContent = matched.available ? 'Add to cart' : 'Sold out';
        }

        // Re-decorate the pack-size ladder. On PDPs product-info.js publishes
        // this after its section re-render; page templates have no product-info,
        // so publish it here (payload shape mirrors product-info.js).
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: panelId, html: document, variant: matched },
          });
        }
      });
    });
  }

  /* =========================================================================
     6. Card Grid — clickable card selector
     ========================================================================= */
  function initCardGrids() {
    document.querySelectorAll('[data-intc-adv-card-grid]').forEach(function (grid) {
      grid.querySelectorAll('[data-intc-adv-card-item]').forEach(function (card) {
        card.addEventListener('click', function () {
          grid.querySelectorAll('[data-intc-adv-card-item]').forEach(function (c) {
            c.classList.remove('intc-adv__card-grid-item--active');
          });
          card.classList.add('intc-adv__card-grid-item--active');

          // Mirror the variant_selector rows: when the block has a scroll
          // target configured, activate the matching product tab (card 1 →
          // tab 1) and smooth-scroll to it.
          var variantIndex = card.getAttribute('data-variant-index');
          var scrollTargetId = card.getAttribute('data-scroll-target');
          if (!scrollTargetId || !variantIndex) return;
          var target = document.getElementById(scrollTargetId);
          if (!target) return;

          target.setAttribute('data-selected-variant', variantIndex);
          activateProductTab(target, variantIndex);

          var yOffset = -80; // account for sticky header
          var y = target.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        });
      });

      // On load, sync the product tabs to the merchant-marked active card
      // (no scroll) — e.g. the 20oz page opens on Maximum even though its
      // card/tab position is 3, not 1.
      var activeCard = grid.querySelector('.intc-adv__card-grid-item--active[data-scroll-target]');
      if (activeCard) {
        var activeIndex = activeCard.getAttribute('data-variant-index');
        var activeTarget = document.getElementById(activeCard.getAttribute('data-scroll-target'));
        if (activeTarget && activeIndex) {
          activeTarget.setAttribute('data-selected-variant', activeIndex);
          activateProductTab(activeTarget, activeIndex);
        }
      }
    });
  }

  /* =========================================================================
     7. Scroll-to CTA buttons
     ========================================================================= */
  function initScrollCtas() {
    document.querySelectorAll('[data-intc-scroll-target]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        var targetId = btn.getAttribute('data-intc-scroll-target');
        var target = document.getElementById(targetId);
        if (!target) return;
        e.preventDefault();
        var yOffset = -80;
        var y = target.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      });
    });
  }

  /* =========================================================================
     Init all
     ========================================================================= */
  ready(function () {
    initSpendCalculators();
    initVariantSelectors();
    initProductTabs();
    initFaqAccordions();
    initVideos();
    initCardGrids();
    initVariantPriceUpdater();
    initScrollCtas();
  });
})();
